import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Button, Progress, Alert, Typography } from 'antd';
import { UploadOutlined, CloudUploadOutlined, CheckCircleOutlined, LoadingOutlined, ThunderboltOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const Upload = ({ onCompressionComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('upload'); // 'upload' or 'processing'
  const fallbackIntervalRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
      }
    };
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile?.type === 'application/zip' || selectedFile?.name.endsWith('.zip')) {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please upload a valid ZIP file containing your images.');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a ZIP file first');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setProgress(0);
    setPhase('upload');
    
    // Start a fallback progress updater in case onUploadProgress doesn't fire
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
    }
    fallbackIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        // Slowly increase progress up to 80% as fallback
        if (prev < 80) {
          return prev + 5;
        }
        return prev;
      });
    }, 500);

    // Cleanup function for intervals
    const cleanup = () => {
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };

    try {
      const token = localStorage.getItem('authToken');
      
      // Step 1: Upload file and get job ID
      const uploadResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/file/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
        onUploadProgress: (progressEvent) => {
          let percentCompleted = 0;
          if (progressEvent.total && progressEvent.total > 0) {
            percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          } else if (progressEvent.loaded > 0) {
            // If we don't know total size but have loaded some bytes, show partial progress
            percentCompleted = Math.min(50, Math.round(progressEvent.loaded / 1024)); // Rough estimate
          }
          // Ensure progress increases (not decreases) and doesn't go above 99% during upload
          percentCompleted = Math.min(99, Math.max(percentCompleted, progress || 0));
          setProgress(percentCompleted);
        },
      });

      // Get job ID from response
      const { jobId, statusUrl } = uploadResponse.data;
      
      // Upload is complete, switch to processing phase
      setPhase('processing');
      setProgress(0); // Start processing progress from 0%
      cleanup(); // Clear fallback interval
      
      // Step 2: Poll job status until completion
      let jobCompleted = false;
      let pollCount = 0;
      const maxPolls = 300; // 5 minutes max (1 second intervals)
      
      const pollJobStatus = async () => {
        try {
          const statusResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${statusUrl}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          const { status, progress: jobProgress } = statusResponse.data;
          
          // Update progress with backend job progress
          if (jobProgress !== undefined) {
            setProgress(prev => Math.min(jobProgress, 100));
          }
          
          if (status === 'completed') {
            jobCompleted = true;
            // Get final result
            const resultResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/file/result/${jobId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            
            const { result } = resultResponse.data;
            const fullDownloadUrl = `${process.env.REACT_APP_API_BASE_URL}${result.downloadUrl}`;
            
            onCompressionComplete({ 
              downloadUrl: fullDownloadUrl, 
              fileName: result.fileName,
              processingTime: result.processingTime,
              originalSize: result.originalSize,
              compressedSize: result.compressedSize
            });
            setError('');
            setUploading(false);
            setProgress(0);
            setPhase('upload');
            cleanup();
          } else if (status === 'failed') {
            jobCompleted = true;
            setError(statusResponse.data.error || 'Processing failed');
            setUploading(false);
            setProgress(0);
            setPhase('upload');
            cleanup();
          } else if (pollCount >= maxPolls) {
            jobCompleted = true;
            setError('Processing timeout - please check status later');
            setUploading(false);
            setProgress(0);
            setPhase('upload');
            cleanup();
          }
        } catch (err) {
          console.error('Polling error:', err);
          // Continue polling on network errors
        }
      };
      
      // Start polling
      const pollInterval = setInterval(() => {
        if (jobCompleted) {
          clearInterval(pollInterval);
          return;
        }
        
        pollCount++;
        pollJobStatus();
        
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          if (!jobCompleted) {
            setError('Processing timeout - please check status later');
            setUploading(false);
            setProgress(0);
          }
        }
      }, 1000); // Poll every second
      
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
      setUploading(false);
      setProgress(0);
      setPhase('upload');
      cleanup();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setFile(null);
    setError('');
  };

  return (
    <div className="glass-panel-dark rounded-3xl overflow-hidden relative group">
      {/* Decorative Glow */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 opacity-60"></div>
      
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/5 relative z-10 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-slate-800/80 border border-slate-700/50 flex items-center justify-center shadow-lg shadow-purple-500/10">
          <UploadOutlined className="text-purple-400 text-xl" />
        </div>
        <div>
          <Title level={4} className="!mb-0.5 !text-slate-100 !font-bold tracking-wide flex items-center gap-3">
            Upload Images <span className="text-purple-400 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/30">.ZIP</span>
          </Title>
          <Text className="text-slate-400 text-xs">
            Powered by COMPRESSAI Engine
          </Text>
        </div>
      </div>

      <div className="p-6 space-y-6 relative z-10">
        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`relative rounded-2xl p-8 text-center cursor-pointer transition-all duration-500 group overflow-hidden
           ${isDragActive
             ? 'bg-slate-800/60 border-2 border-dashed border-purple-500 scale-[1.01] shadow-[0_0_30px_rgba(168,85,247,0.2)]'
             : file
               ? 'bg-slate-800/40 border border-slate-700/50 hover:bg-slate-800/60'
               : 'bg-slate-800/20 border-2 border-dashed border-slate-700 hover:border-purple-500/50 hover:bg-slate-800/40'}`}
        >
          <input {...getInputProps()} />
          
          {/* Animated Background for Drag Active */}
          {isDragActive && (
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 animate-pulse"></div>
          )}
          
          {file ? (
            <div className="space-y-5 relative z-10 animate-fade-in-up">
              <div className="w-20 h-20 bg-emerald-500/20 border border-emerald-500/50 rounded-2xl flex items-center justify-center mx-auto shadow-[0_0_20px_rgba(16,185,129,0.2)] animate-pulse-glow">
                <CheckCircleOutlined className="text-emerald-400 text-4xl" />
              </div>
              <div>
                <Text strong className="text-xl text-slate-200 block truncate max-w-xs mx-auto">{file.name}</Text>
                <div className="flex items-center justify-center gap-3 mt-4">
                  <span className="text-slate-400 text-sm font-medium bg-slate-900/50 px-3 py-1 rounded-full border border-slate-700/50">
                    {formatFileSize(file.size)}
                  </span>
                  <button onClick={removeFile} className="text-red-400 hover:text-red-300 text-sm font-medium bg-red-500/10 hover:bg-red-500/20 px-3 py-1 rounded-full border border-red-500/20 transition-colors flex items-center gap-1">
                    <CloseCircleOutlined className="mr-1.5" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 relative z-10">
              <div className={`w-14 h-14 mx-auto rounded-xl border flex items-center justify-center transition-all duration-500
                ${isDragActive 
                   ? 'bg-purple-500/20 border-purple-500/50 text-purple-400 scale-110 shadow-[0_0_20px_rgba(168,85,247,0.3)]' 
                   : 'bg-slate-900/50 border-slate-700/50 text-slate-500 group-hover:text-purple-400 group-hover:border-purple-500/30 group-hover:bg-slate-800/80 shadow-inner'}`}>
                <CloudUploadOutlined className={`text-3xl transition-transform duration-500 ${isDragActive ? '-translate-y-1' : ''}`} />
              </div>
              <div>
                <Text strong className="text-lg text-slate-200 block tracking-wide">
                  {isDragActive ? 'Release to upload ZIP' : 'Drag & drop ZIP file here'}
                </Text>
                <Text className="text-sm text-slate-300 mt-2 block">
                  or click to browse from your computer
                </Text>
              </div>
              <div className="flex items-center justify-center gap-3 pt-4">
                <span className="text-xs font-semibold text-slate-400 bg-slate-900/50 px-3 py-1 rounded-md border border-slate-800">MAX 200MB</span>
                <span className="text-xs font-semibold text-slate-400 bg-slate-900/50 px-3 py-1 rounded-md border border-slate-800">ZIP ONLY</span>
              </div>
            </div>
          )}
        </div>

        {/* Progress */}
        {uploading && (
          <div className="space-y-4 bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 backdrop-blur-md animate-fade-in-up">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LoadingOutlined className="text-purple-400 text-xl mr-1.5" />
                 <Text strong className="text-slate-300 tracking-wide text-sm uppercase">
                   {phase === 'upload' ? 'Uploading...' : 'Analyzing & Compressing...'}
                 </Text>
              </div>
              <Text strong className="text-purple-400 text-lg font-mono">{progress}%</Text>
            </div>
            <Progress
              percent={progress}
              status={progress === 100 ? 'success' : 'active'}
              showInfo={false}
              strokeColor={{
                '0%': '#8b5cf6',
                '100%': '#6366f1',
              }}
              trailColor="rgba(30, 41, 59, 0.5)"
              className="mb-0"
              strokeWidth={8}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            className="rounded-xl animate-fade-in-up"
          />
        )}

        {/* Upload Button */}
        <Button
          type="primary"
          icon={<ThunderboltOutlined className="text-lg" />}
          onClick={handleUpload}
          disabled={!file || uploading}
          loading={uploading}
          block
          size="large"
          className="h-14 rounded-xl font-bold text-base uppercase tracking-wider relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Processing Data...' : 'Initialize Compression'}
          {!uploading && file && (
            <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full border-dance transition-transform duration-1000 ease-in-out"></div>
          )}
        </Button>

      </div>
    </div>
  );
};

export default Upload;